import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Save } from "lucide-react";

const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
const STATES = ["Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Delhi","Goa","Gujarat","Haryana","Himachal Pradesh","Jammu & Kashmir","Jharkhand","Karnataka","Kerala","Madhya Pradesh","Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland","Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana","Tripura","Uttar Pradesh","Uttarakhand","West Bengal","Andaman & Nicobar","Chandigarh","Dadra & Nagar Haveli and Daman & Diu","Lakshadweep","Puducherry","Ladakh"];
const LANGUAGES = ["Hindi", "English", "Marathi", "Sanskrit", "Tamil", "Bengali", "Gujarati", "Kannada", "Telugu", "Malayalam", "Punjabi"];

const schema = z.object({
  organization_id: z.string().min(1, "Required"),
  name: z.string().min(2, "Min 2 chars"),
  vibhag_pramukh: z.string().optional().or(z.literal("")),
  primary_language: z.string().optional().or(z.literal("")),
  languages: z.array(z.string()).default([]),
  established_date: z.string().optional().or(z.literal("")),
  isbn_prefix: z.string().optional().or(z.literal("")),
  msme_number: z.string().optional().or(z.literal("")),
  registration_number: z.string().optional().or(z.literal("")),
  email: z.string().email("Invalid email").optional().or(z.literal("")),
  phone: z.string().regex(/^[6-9]\d{9}$/, "10-digit Indian mobile").optional().or(z.literal("")),
  website_url: z.string().url("Invalid URL").optional().or(z.literal("")),
  use_org_address: z.boolean().default(false),
  full_address: z.string().optional().or(z.literal("")),
  is_gst_registered: z.boolean().default(false),
  gstin: z.string().optional().or(z.literal("")),
  hsn_sac_code: z.string().optional().or(z.literal("")),
  invoice_prefix: z.string().optional().or(z.literal("")),
  place_of_supply_state: z.string().optional().or(z.literal("")),
}).superRefine((v, ctx) => {
  if (!v.is_gst_registered) return;
  if (!v.gstin || !GSTIN_RE.test(v.gstin)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["gstin"], message: "Valid 15-char GSTIN required when GST registered" });
  }
  if (!v.invoice_prefix || !/^[A-Z0-9]{2,8}$/.test(v.invoice_prefix)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["invoice_prefix"], message: "2-8 uppercase letters/digits (e.g. KAL)" });
  }
  if (!v.place_of_supply_state) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["place_of_supply_state"], message: "Required when GST registered" });
  }
});

function Required() { return <span className="text-rose-600">*</span>; }

export default function PublicationHouseForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState([]);
  const [orgAddress, setOrgAddress] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const { register, handleSubmit, watch, setValue, reset, formState: { errors } } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { languages: [], use_org_address: false, is_gst_registered: false },
  });

  const selectedOrg = watch("organization_id");
  const langs = watch("languages") || [];
  const useOrg = watch("use_org_address");
  const isGstReg = watch("is_gst_registered");

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/organizations/active-list"); setOrgs(data); } catch { /* */ }
    })();
  }, []);

  useEffect(() => {
    if (!selectedOrg) return;
    (async () => {
      try { const { data } = await api.get(`/organizations/${selectedOrg}`); setOrgAddress(data.address || ""); } catch { /* */ }
    })();
  }, [selectedOrg]);

  useEffect(() => { if (useOrg && orgAddress) setValue("full_address", orgAddress); }, [useOrg, orgAddress, setValue]);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await api.get(`/publication-houses/${id}`);
        reset({
          organization_id: data.organization_id,
          name: data.name || "",
          vibhag_pramukh: data.vibhag_pramukh || "",
          primary_language: data.primary_language || "",
          languages: data.languages || [],
          established_date: data.established_date ? data.established_date.slice(0, 10) : "",
          isbn_prefix: data.isbn_prefix || "",
          msme_number: data.msme_number || "",
          registration_number: data.registration_number || "",
          email: data.email || "",
          phone: data.phone || "",
          website_url: data.website_url || "",
          use_org_address: !!data.use_org_address,
          full_address: data.full_address || "",
          is_gst_registered: !!data.is_gst_registered,
          gstin: data.gstin || "",
          hsn_sac_code: data.hsn_sac_code || "",
          invoice_prefix: data.invoice_prefix || "",
          place_of_supply_state: data.place_of_supply_state || "",
        });
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    })();
  }, [id, isEdit, reset]);

  const toggleLang = (lng) => {
    const next = langs.includes(lng) ? langs.filter((x) => x !== lng) : [...langs, lng];
    setValue("languages", next);
  };

  const onSubmit = async (values) => {
    setSubmitting(true);
    try {
      const payload = { ...values };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      if (isEdit) {
        await api.put(`/publication-houses/${id}`, payload);
        toast.success("Publication house updated");
      } else {
        await api.post("/publication-houses", payload);
        toast.success("Publication house created");
      }
      navigate("/publication-houses");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="pub-form-page" className="mx-auto max-w-4xl">
      <Button variant="ghost" size="sm" asChild className="mb-3 rounded-sm text-slate-600">
        <Link to="/publication-houses"><ArrowLeft size={14} className="mr-1" />Back</Link>
      </Button>
      <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
        {isEdit ? "Edit Publication House" : "Create Publication House"}
      </h1>

      <form onSubmit={handleSubmit(onSubmit)} className="mt-8 space-y-6 rounded-sm border border-slate-200 bg-white p-6">
        <Section title="Hierarchy">
          <Field label="Parent Organization" required error={errors.organization_id?.message} full>
            <Select value={selectedOrg || ""} onValueChange={(v) => setValue("organization_id", v, { shouldValidate: true })}>
              <SelectTrigger data-testid="pub-org-select" className="rounded-sm"><SelectValue placeholder="Select organization" /></SelectTrigger>
              <SelectContent>
                {orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        </Section>

        <Section title="General">
          <Field label="Publication Name" required error={errors.name?.message}>
            <Input data-testid="pub-name-input" {...register("name")} className="rounded-sm" />
          </Field>
          <Field label="Established Date" error={errors.established_date?.message}>
            <Input type="date" data-testid="pub-est-input" {...register("established_date")} className="rounded-sm" />
          </Field>
          <Field label="Website" error={errors.website_url?.message}>
            <Input data-testid="pub-web-input" {...register("website_url")} className="rounded-sm" placeholder="https://" />
          </Field>
          <Field label="Vibhag Pramukh (Head of Dept)">
            <Input data-testid="pub-pramukh-input" {...register("vibhag_pramukh")} className="rounded-sm" />
          </Field>
        </Section>

        <Section title="Registration">
          <Field label="Registration / License Number">
            <Input data-testid="pub-regno-input" {...register("registration_number")} className="rounded-sm font-mono" />
          </Field>
          <Field label="MSME Number (optional)">
            <Input data-testid="pub-msme-input" {...register("msme_number")} className="rounded-sm font-mono" />
          </Field>
          <Field label="ISBN Publisher Prefix">
            <Input data-testid="pub-isbn-input" {...register("isbn_prefix")} className="rounded-sm font-mono" placeholder="978-81-XXXXX" />
          </Field>
        </Section>

        <Section title="Language & Contact">
          <Field label="Primary Language">
            <Select value={watch("primary_language") || ""} onValueChange={(v) => setValue("primary_language", v)}>
              <SelectTrigger data-testid="pub-primary-lang" className="rounded-sm"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{LANGUAGES.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Email" error={errors.email?.message}>
            <Input type="email" data-testid="pub-email-input" {...register("email")} className="rounded-sm" />
          </Field>
          <Field label="Phone (Indian 10-digit)" error={errors.phone?.message}>
            <Input data-testid="pub-phone-input" {...register("phone")} className="rounded-sm font-mono" placeholder="9876543210" />
          </Field>
          <Field label="Languages (multi-select)" full>
            <div className="flex flex-wrap gap-2 rounded-sm border border-slate-200 p-3">
              {LANGUAGES.map((l) => (
                <label key={l} className={`flex cursor-pointer items-center gap-2 rounded-sm border px-2 py-1 text-xs ${langs.includes(l) ? "border-[#002FA7] bg-indigo-50 text-[#002FA7]" : "border-slate-200 bg-white text-slate-700"}`}>
                  <Checkbox checked={langs.includes(l)} onCheckedChange={() => toggleLang(l)} data-testid={`pub-lang-${l}`} className="h-3.5 w-3.5 rounded-sm" />
                  {l}
                </label>
              ))}
            </div>
          </Field>
        </Section>

        <Section title="Address">
          <div className="sm:col-span-2 flex items-center justify-between rounded-sm border border-slate-200 bg-slate-50 p-3">
            <div>
              <div className="text-sm font-medium text-slate-900">Use Organization Address</div>
              <div className="text-xs text-slate-500">Auto-fill from parent organization.</div>
            </div>
            <Switch data-testid="pub-use-org-addr" checked={!!useOrg} onCheckedChange={(v) => setValue("use_org_address", v)} />
          </div>
          <Field label="Full Address" full>
            <Textarea data-testid="pub-address-input" {...register("full_address")} rows={2} disabled={useOrg} className="rounded-sm" />
          </Field>
        </Section>

        <Section title="GST & Invoicing">
          <div className="sm:col-span-2 flex items-center justify-between rounded-sm border border-slate-200 bg-slate-50 p-3">
            <div>
              <div className="text-sm font-medium text-slate-900">GST Registered</div>
              <div className="text-xs text-slate-500">Toggle ON if this publication house has its own GSTIN. Tax invoices will be auto-generated; otherwise non-GST receipts.</div>
            </div>
            <Switch data-testid="pub-gst-toggle" checked={!!isGstReg} onCheckedChange={(v) => setValue("is_gst_registered", v, { shouldValidate: true })} />
          </div>
          {isGstReg && (
            <>
              <Field label="GSTIN" required error={errors.gstin?.message}>
                <Input data-testid="pub-gstin-input" {...register("gstin", { setValueAs: (v) => (v || "").toUpperCase().trim() })} className="rounded-sm font-mono" placeholder="22AAAAA0000A1Z5" maxLength={15} />
              </Field>
              <Field label="Place of Supply (State)" required error={errors.place_of_supply_state?.message}>
                <Select value={watch("place_of_supply_state") || ""} onValueChange={(v) => setValue("place_of_supply_state", v, { shouldValidate: true })}>
                  <SelectTrigger data-testid="pub-pos-select" className="rounded-sm"><SelectValue placeholder="Select state" /></SelectTrigger>
                  <SelectContent className="max-h-64">{STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Invoice Prefix" required error={errors.invoice_prefix?.message}>
                <Input
                  data-testid="pub-invoice-prefix"
                  {...register("invoice_prefix", { setValueAs: (v) => (v || "").toUpperCase().trim() })}
                  className="rounded-sm font-mono"
                  placeholder="KAL"
                  maxLength={8}
                />
              </Field>
              <Field label="Default HSN/SAC Code">
                <Input
                  data-testid="pub-hsn-input"
                  {...register("hsn_sac_code", { setValueAs: (v) => (v || "").trim() })}
                  className="rounded-sm font-mono"
                  placeholder="4902 (printed periodicals) or 998431 (online)"
                />
              </Field>
              <div className="sm:col-span-2 rounded-sm border border-amber-200 bg-amber-50/60 px-3 py-2 text-xs text-amber-800">
                Invoice format will be <span className="font-mono font-semibold">{(watch("invoice_prefix") || "INV").toUpperCase()}/&lt;FY&gt;/&lt;6-digit-seq&gt;</span> · FY auto-rolls every 1st April · always IGST.
              </div>
            </>
          )}
        </Section>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => navigate("/publication-houses")} className="rounded-sm">Cancel</Button>
          <Button type="submit" disabled={submitting} data-testid="pub-submit-btn" className="rounded-sm bg-[#002FA7] hover:bg-[#00227A]">
            <Save size={14} className="mr-1.5" />
            {submitting ? "Saving…" : isEdit ? "Save changes" : "Create"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="mb-4 border-b border-slate-200 pb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">{title}</div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{children}</div>
    </div>
  );
}

function Field({ label, required, error, full, children }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <Label className="mb-1 block text-sm font-medium text-slate-700">
        {label} {required && <Required />}
      </Label>
      {children}
      {error && <p className="mt-1 text-xs text-rose-600">{error}</p>}
    </div>
  );
}
